import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import {
    CaretSortIcon,
    CheckIcon,
    UpdateIcon,
} from '@radix-ui/react-icons';

import { Button } from '../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "../ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '../ui/form';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../ui/popover';

import { useForm } from 'react-hook-form';
import api from '../../lib/api';
import { toast } from '../ui/use-toast';


const ArchiveClubDialog = ({ close, clubs, isOpen, onClubArchived }) => {
    const form = useForm()
    const [isLoading, setIsLoading] = useState(false);
    const [comboboxOpen, setComboboxOpen] = useState(false);

    const [selectedClub, setSelectedClub] = useState();

    useEffect(() => {
        form.reset();
        setSelectedClub(null);
    }, [isOpen]);

    const sendArchiveRequest = async (e) => {
        if (isLoading) return; // If loading, return
        e.preventDefault();
        setIsLoading(true); // Set loading to true

        // Send request to archive club
        api.post(`https://skhrf-api.cmdigital.se/v1/clubs/archive`, { clubId: selectedClub })
            .then(response => {
                setIsLoading(false); // Request successful, set loading to false
                // Get the club name
                const clubName = clubs.find(club => club.id === selectedClub).name;

                toast({
                    title: 'Förening arkiverad.',
                    description: `${clubName} har arkiverats i systemet.`,
                });
                onClubArchived(); // Call the callback to fetch clubs
                close(false);
            })
            .catch(error => {
                console.error('Error archiving club:' + error?.response?.data?.error); // Log
                setIsLoading(false); // Error occurred, set loading to false
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: error?.response?.data?.error,
                });
            });
    }

    return (
        <DialogContent>
            <DialogHeader className='text-left'>
                <DialogTitle>Arkivera förening</DialogTitle>
                <DialogDescription>Denna åtgärd kan inte ångras!</DialogDescription>
            </DialogHeader>
            <Form {...form} >
                <form onSubmit={sendArchiveRequest} className="flex flex-col gap-6">
                    <FormField
                        control={form.control}
                        name="clubName"
                        render={({ field }) => (
                            <FormItem className='space-y-0 grid gap-2'>
                                <FormLabel>Förening</FormLabel>
                                <Popover modal={true} open={comboboxOpen} onOpenChange={setComboboxOpen}>
                                    <PopoverTrigger asChild>
                                        <FormControl>
                                            <Button
                                                variant="outline"
                                                role="combobox"
                                                className={cn(
                                                    "w-full justify-between",
                                                    !field.value && "text-muted-foreground"
                                                )}
                                            >
                                                {field.value
                                                    ? clubs.find(
                                                        (club) => club.id === field.value
                                                    )?.name
                                                    : "Välj förening"}
                                                <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            </Button>
                                        </FormControl>
                                    </PopoverTrigger>
                                    <PopoverContent className="popover-content-width-full p-0">
                                        <Command>
                                            <CommandInput placeholder="Sök förening..." />
                                            <CommandList>
                                                <CommandEmpty>Ingen förening hittades.</CommandEmpty>
                                                <CommandGroup>
                                                    {clubs
                                                        .filter((club) => club.archived_at === null)
                                                        .map((club) => (
                                                            <CommandItem
                                                                className='cursor-pointer'
                                                                value={club.id}
                                                                key={club.id}
                                                                onSelect={() => {
                                                                    form.setValue("clubName", club.id);
                                                                    setSelectedClub(club.id);
                                                                    setComboboxOpen(false);
                                                                }}
                                                            >
                                                                <CheckIcon
                                                                    className={cn(
                                                                        "mr-2 h-4 w-4",
                                                                        club.id === field.value ? "opacity-100" : "opacity-0"
                                                                    )}
                                                                />
                                                                {club.name}
                                                            </CommandItem>
                                                        ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className='grid grid-cols-2 gap-3'>
                        <DialogClose asChild>
                            <Button type='button' variant='secondary'>Avbryt</Button>
                        </DialogClose>
                        <Button type="submit" variant='destructive' className="w-full" disabled={isLoading}>
                            {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Arkivera förening</>}
                        </Button>
                    </div>
                </form>
            </Form>
        </DialogContent>
    );
};

export default ArchiveClubDialog;